import isNil from 'lodash/isNil';
import {
	type CacheConfig,
	type GraphQLResponse,
	type GraphQLSingularResponse,
	Observable,
	type RequestParameters,
	type Variables,
} from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { putExtensions } from '@atlassian/relay-capture-extensions';
import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { CompletedQueryPromisesMap, QueryPromisesMap } from '@atlassian/relay-query-promises';
import { addTraceId } from '@atlassian/relay-traceid';
import { getClientDispatcher } from '@atlassian/server-to-client-comms/client-dispatcher';
import { registerEvent } from '@atlassian/server-to-client-comms/server-receiver';

import { getHttpErrorDetails } from '../getHttpErrorDetails';
import { RelayHttpError } from '../httpError';
import { buildGraphqlSmartFetch } from '../multipart-fetch/buildGraphqlSmartFetch';
import { processMultipartPromise } from '../multipart-fetch/processMultipartPromise';
import type { GraphQLMultipartResponsePayload } from '../multipart-fetch/types';
import { getRetryAttemptsForQuery } from '../utils/getRetryAttemptsForQuery';
import { retryOnError } from '../utils/retryOnError';
import { shouldRetry } from '../utils/shouldRetry';
import { getTraceIdFromExtensions } from '../utils/trace';
import type { ExternalDependencies } from './ExternalDependencies';

const isSsrHandoverIncrementsEnabled = () => Boolean((globalThis as any).__SSR_HANDOVER_INCREMENTS);

const moduleID = 'relay-fetch';

export const fetch = (
	request: RequestParameters,
	variables: Variables,
	cacheConfig: CacheConfig,
	dependencies?: ExternalDependencies,
): Observable<GraphQLResponse> => {
	if (
		dependencies?.didPreflightAuthenticationCheckTriggerRedirect?.(
			request,
			variables,
			cacheConfig,
		) === true
	) {
		// Jira's preflight check starts login navigation as a side effect. Keep Relay pending
		// while the redirect takes over instead of starting a fetch with a stale auth state.
		return Observable.from(new Promise<GraphQLResponse>(() => {}));
	}

	const { name: queryName, operationKind } = request;
	const loggingMetadata = {
		queryName,
		operationKind,
	};
	const loggingAndMetrics = dependencies?.getLoggingAndMetricsHelper?.(loggingMetadata);

	// Per-query opt-in (cacheConfig.metadata.captureErrors) to forward this operation's response to
	// the product's capture callback, if one is provided. Whether to actually capture (feature gates,
	// etc.) is a product concern owned by the callback itself, not by this platform code.
	const shouldCaptureErrors =
		cacheConfig?.metadata?.captureErrors === true && !dependencies?.isServer;
	let responseHeaders: Headers | undefined;
	// A payload already chose this operation's trace id. A later transport error must not append
	// the header id as well.
	let responseTraceIdCaptured = false;

	function responseHeaderTraceId(): string | undefined {
		return responseHeaders?.get('atl-traceid') ?? undefined;
	}

	function augmentResponse(response: GraphQLSingularResponse) {
		const traceIdFromHeader = responseHeaderTraceId();
		addTraceId(queryName, getTraceIdFromExtensions(response) ?? traceIdFromHeader, variables);
		putExtensions(queryName, response.extensions);
		responseTraceIdCaptured = true;
	}

	function recordAvailableHeaderTraceId() {
		if (responseTraceIdCaptured) {
			return;
		}
		const traceId = responseHeaderTraceId();
		if (traceId === undefined) {
			return;
		}
		responseTraceIdCaptured = true;
		addTraceId(queryName, traceId, variables);
	}

	// TODO: This needs to be changed by removing each Product, and only if all keys are empty final output is null
	function enforceNonNullability(response: GraphQLSingularResponse) {
		if (
			response.data &&
			// DO NOT ADD TO THIS CONDITION.
			// Doing so can make service timeouts in non-critical dependencies result in a complete breakage of experiences.
			// If requesting a namespaced field, flatten it - e.g. "query { foo { bar {" should become "query { foo_bar {".
			// See https://atlassian.slack.com/archives/C01E2MPPLE4/p1780367270438419.
			(response.data.jira === null ||
				response.data.customerService === null ||
				response.data.graphStore === null)
		) {
			// Enforce non-nullability of the jira and customerService namespaces. This is an intermediary solution to
			// prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
			// to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
			// @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Data
			response.data = null;
		}
	}

	return Observable.create<GraphQLResponse>((sink) => {
		try {
			const requestID =
				'cacheID' in request && request.cacheID != null ? request.cacheID : request.id;
			// Unique identifier for the server-to-client channel.
			const sinkID = `${moduleID}-${requestID}`;

			let resolveCompletedResponse: () => void;
			const completedResponse: Promise<void> = new Promise((resolve) => {
				resolveCompletedResponse = resolve;
			});

			const finalise = () => {
				resolveCompletedResponse();
				sink.complete();
			};

			const handleResponse = (response: GraphQLMultipartResponsePayload) => {
				let payload: GraphQLSingularResponse | null = null;
				if ('incremental' in response && response.incremental) {
					if (response.incremental.length) {
						// Relay is expecting the contents of this array rather than the entire array itself. Also, Relay doesn't really care about tracking
						// the pending versus completed values, so we can just return the array without the extra account keeping.
						// @ts-expect-error It's hard to type this correctly because the payload sometimes has errors, sometimes not. Relay accounts for it,
						// TypeScript can't handle the polymorphism.
						payload = response.incremental;
					} else if (response.hasNext === false) {
						// It's possible for the server to send back an empty final increment.
						// Attempt to make Relay happy.
						payload = {
							data: null,
							extensions: { is_final: true },
						};
					}
				} else {
					// @ts-expect-error Typescript isn't happy that sometimes errors is undefined, but relay will manage just fine.
					payload = response;
				}

				// Notify observers that we have more data!
				if (payload) {
					sink.next(payload);
				}

				// Close the stream if there are no more responses.
				if ('hasNext' in response && response.hasNext === false) {
					finalise();
				}
			};

			// handles getting multipart response
			const getData = () => {
				const endpoint = getAggEndpoint(request, cacheConfig);

				const body = getRequestBody(request, variables);
				let options: RequestInit = { body };

				if (dependencies?.getHeaders) {
					const headers = dependencies.getHeaders({ endpoint, options, request, variables });
					options = { ...options, headers: { ...options.headers, ...headers } };
				}
				if (dependencies?.getAbortSignal) {
					options.signal = dependencies?.getAbortSignal();
				}

				const fetchOverNetwork: () => Promise<Response> = buildGraphqlSmartFetch({
					url: dependencies?.setEndpoint ? dependencies?.setEndpoint(endpoint) : endpoint,
					options,
					variables,
					request,
					isServer: dependencies?.isServer || false,
					cacheConfig,
				});

				const earlyScriptPromise = dependencies?.earlyScriptPromise?.(request, variables);

				let fetchFn = fetchOverNetwork;
				let initialSource = fetchFn;
				if (dependencies?.withErrorHandling) {
					const withErrorHandling = dependencies.withErrorHandling;
					initialSource = () =>
						!dependencies?.isServer && earlyScriptPromise
							? withErrorHandling(earlyScriptPromise)
							: withErrorHandling(fetchFn());
				} else if (fg('relay_environment_-_add_status-code_to_agg_errors')) {
					// Preserve existing custom handlers and their retry semantics (e.g. Jira).
					const checkResponse = (response: Response): Response => {
						const details = getHttpErrorDetails(response);
						if (details) {
							throw new RelayHttpError(details);
						}
						return response;
					};
					fetchFn = () => fetchOverNetwork().then(checkResponse);
					initialSource = () =>
						!dependencies?.isServer && earlyScriptPromise
							? earlyScriptPromise.then(checkResponse)
							: fetchFn();
				}

				const responses = processMultipartPromise(
					() =>
						retryOnError(initialSource, {
							retryFunc: fetchFn,
							retryPredicate: shouldRetry,
							retryAttempts: getRetryAttemptsForQuery(request),
							onRetry: loggingAndMetrics?.logErrorRetry,
						}),
					dependencies?.onFetchError,
					(headers) => {
						responseHeaders = headers;
					},
				);

				// Recreate a bizarre behaviour that will cause the observable to hang indefinitely when the user is not authenticated, presumably because we redirect the user to log in...
				// This should be revised; see https://jplat.atlassian.net/browse/MAGMA-3087
				let responseShouldHang = false;

				let firstResponseUsed = false;
				let resolveFirstResponse: () => void;
				const firstResponse: Promise<void> = new Promise((resolve) => {
					resolveFirstResponse = resolve;
				});

				responses.subscribe({
					start: () => {
						if (requestID != null) {
							if (dependencies?.isServer) {
								// so that SSR SPA knows it is safe to render
								const renderAfterThisResponse = isSsrHandoverIncrementsEnabled()
									? firstResponse
									: completedResponse;
								QueryPromisesMap.set(requestID, renderAfterThisResponse);
							} else {
								QueryPromisesMap.set(requestID, firstResponse);
							}
							CompletedQueryPromisesMap.set(requestID, completedResponse);
						}
					},
					next: (r) => {
						let payload = r;
						let response = (payload || {}) as GraphQLSingularResponse;

						if (dependencies?.isServer && isSsrHandoverIncrementsEnabled() && firstResponseUsed) {
							// Send the response payload to a side-channel.
							// TODO MAGMA-3204: improve robustness. Should be sending ID of module to communicate with, sinkID should be an argument.
							registerEvent({
								id: sinkID,
								name: 'default',
								args: [response],
							});

							if ('hasNext' in response && response.hasNext === false) {
								finalise();
							}

							// Don't send any further responses to the fetch subscriber, since we'll be sending them through a side-channel.
							return;
						}

						if (!firstResponseUsed) {
							firstResponseUsed = true;
							// according to spec, we can assume the first response is a GraphQLSingularResponse.
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const singularResponse = response as GraphQLSingularResponse;

							augmentResponse(singularResponse);

							if (
								shouldCaptureErrors &&
								typeof dependencies?.captureResponseErrors === 'function'
							) {
								dependencies.captureResponseErrors(request, singularResponse);
							}

							if (
								dependencies?.validateResponse &&
								!dependencies.validateResponse(request, singularResponse)
							) {
								dependencies?.onInvalidResponse?.(request, singularResponse);
								responseShouldHang = true;
								return;
							}

							enforceNonNullability(singularResponse);

							// write cache query response on SSR
							// only write cache query response once or else this will break the assumption of graphql response being singular response
							if (
								dependencies?.isServer &&
								requestID != null &&
								typeof dependencies?.queryResponseCache !== 'undefined'
							) {
								if ('appendWithMetadata' in dependencies.queryResponseCache) {
									dependencies.queryResponseCache.appendWithMetadata(
										requestID,
										variables,
										singularResponse,
										loggingMetadata,
									);
								} else {
									dependencies.queryResponseCache.setWithMetadata(
										requestID,
										variables,
										singularResponse,
										loggingMetadata,
									);
								}
							}
						}

						handleResponse(response);

						// signal that the initial response has been received
						resolveFirstResponse();

						// After the initial response, further responses are sent through a side-channel.
						if (dependencies?.isServer && isSsrHandoverIncrementsEnabled()) {
							sink.complete();
						}
					},
					error: (error: Error) => {
						if (!firstResponseUsed) {
							firstResponseUsed = true;
							// todo MAGMA-3172: check control flows for fetch failure cases on server + client. Seems weird that
							// the previous construction of the function would not send a signal that the response was received
							// in the error case...
							// resolveFirstResponse();
						}
						recordAvailableHeaderTraceId();
						loggingAndMetrics?.logError(error);
						sink.error(error);
					},
					complete: () => {
						if (responseShouldHang) {
							return;
						}
						finalise();
					},
				});
			};

			// On client...
			if (!dependencies?.isServer && requestID != null) {
				// read cache from window.SPA_STATE if available
				const ssrCache = dependencies?.queryResponseCache?.get(requestID, variables);
				if (!isNil(ssrCache)) {
					// The cached payload is seeded into Relay below without passing through
					// validateResponse, so capture its errors here instead.
					if (
						shouldCaptureErrors &&
						!Array.isArray(ssrCache) &&
						typeof dependencies?.captureResponseErrors === 'function'
					) {
						dependencies.captureResponseErrors(request, ssrCache as GraphQLSingularResponse);
					}
					dependencies?.queryResponseCache?.delete(requestID, variables);
					sink.next(ssrCache);
					if (isSsrHandoverIncrementsEnabled() && 'hasNext' in ssrCache && ssrCache.hasNext) {
						// TODO MAGMA-3017: what about SSR fallback mode, or scenarios where SSR has partially failed?
						// TODO MAGMA-3204: improve robustness. Should be using a middle-man module rather than client dispatcher directly.
						// Receive response events as they are streamed from the server.
						getClientDispatcher().register(sinkID, handleResponse);
						getClientDispatcher().onAbort(sinkID, ({ reason }) => {
							if (!sink.closed) {
								dependencies?.handleAbort?.(request, reason);
								// if server throws an exception (such as SSR hanging up due to deadline exceeded)
								// refetch multipart data again via network
								getData();
							}
						});
					} else {
						finalise();
					}
					return;
				}
			}

			getData();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			recordAvailableHeaderTraceId();
			loggingAndMetrics?.logError(error);
			sink.error(error);
		}
	});
};
